    import React, { useState, useRef, useEffect } from "react";
    import { useParams } from "react-router-dom";
    import { doc, onSnapshot, updateDoc } from "firebase/firestore";
    import { db } from "../firebase";
    import Header from "../components/Header";
    import TimerDisplay from "../components/TimerDisplay";
    import TimerControls from "../components/TimerControls";
    import Footer from "../components/Footer";
    import { parseTime, formatTime } from "../components/timeHelpers";
    import ThemeModal from "../components/ThemeModal";
    import CustomTimerModal from "../components/CustomTimerModal.js";
    import { useSharedTimer } from "../components/SharedTimer.js";
    import { motion, AnimatePresence } from "framer-motion";
    import { useNavigate, useLocation } from "react-router-dom";
    import NotepadModal from "../components/NotepadModal";
    import useTimerPictureInPicture from "../components/useTimerPictureInPicture";
    import { getTimerDisplayLabel } from "../components/timeHelpers";
    export default function TimerRoomPage({ themeKey, setThemeKey, themes }) {
    const { roomId } = useParams();
    const [notFound, setNotFound] = useState(false);
    const location = useLocation();
    useEffect(() => {
      if (location.state && location.state.notification) {
        setShowNotif(true);
        // Remove the notification from history so it doesn't appear again if user reloads
        window.history.replaceState({}, document.title);
      }
    }, [location.state]);    
    const [displayMode, setDisplayMode] = useState("text");
    const [mode, setMode] = useState("settime");
    const [editing, setEditing] = useState(false);
    const [inputValue, setInputValue] = useState(formatTime(25 * 60));
    const [baseSeconds, setBaseSeconds] = useState(25 * 60);
    const [running, setRunning] = useState(false);
    const inputRef = useRef();
    const alarmAudioRef = useRef();
    const [previewThemeKey, setPreviewThemeKey] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const activeThemeKey = previewThemeKey || themeKey;
    const theme = themes[activeThemeKey] || themes["coffee"];
    const [customModalOpen, setCustomModalOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [currentPhase, setCurrentPhase] = useState("timer");
    const [timerPairs, setTimerPairs] = useState([
        { timer: "00:00:00", break: "00:00:00", timerLabel: "TIMER", breakLabel: "BREAK" }
    ]);
    const [loopTimers, setLoopTimers] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState(null);
    const navigate = useNavigate();
    const [, forceRerender] = useState(0); // For re-rendering every second
    // Admin detection
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminUid, setAdminUid] = useState(null);
    const [showNotif, setShowNotif] = useState(false);
    const [notepadModalOpen, setNotepadModalOpen] = useState(false);

    // Use shared timer for sync
    const { sharedState } = useSharedTimer(
        roomId,
        {
        running,
        baseSeconds,
        mode,
        timerPairs,
        loopTimers,
        selectedPreset,
        currentStep,
        currentPhase,
        },
        isAdmin
    );

    // Keep UI state in sync with Firestore
    useEffect(() => {
        if (!roomId) return;
        const ref = doc(db, "timers", roomId);
        const unsub = onSnapshot(ref, (snap) => {
        if (!snap.exists()) {
            setNotFound(true);
            return;
        }

        const data = snap.data();
        setAdminUid(data.adminUid);
        setRunning(data.running);
        setBaseSeconds(data.baseSeconds);
        setMode(data.mode);
        setTimerPairs(data.timerPairs || []);
        setLoopTimers(!!data.loopTimers);
        setSelectedPreset(data.selectedPreset);
        setCurrentPhase(data.currentPhase || "timer");
        setCurrentStep(data.currentStep || 0);
        setInputValue(formatTime(data.baseSeconds || 0));
        setNotFound(false);
        });
        return () => unsub();
    }, [roomId]);

    const sharedRunning = !!sharedState?.running;

    useEffect(() => {
        if (!sharedRunning) return;
        const interval = setInterval(() => {
          forceRerender(x => x + 1);
        }, 1000);
        return () => clearInterval(interval);
      }, [sharedRunning]);      
      

    // Check admin
    useEffect(() => {
        const myUid = localStorage.getItem("tufftimer_uid");
        setIsAdmin(adminUid && myUid === adminUid);
    }, [adminUid]);
    
    // Only admin can send updates
    async function updateRoom(fields) {
        if (!isAdmin) return;
        const ref = doc(db, "timers", roomId);
        await updateDoc(ref, {
            ...fields,
            lastUpdated: Date.now()
        });
    }
    
    function startCustomSequenceRoom() {
        if (!isAdmin) return;
        const first = timerPairs?.[0];
        const sec = parseTime(first?.timer || "00:00");
        updateRoom({
          mode: "custom",
          selectedPreset: null,
          currentStep: 0,
          currentPhase: "timer",
          baseSeconds: sec,
          timerSeconds: sec,
          timerPairs,          // persist what was configured in the modal
          loopTimers,
          running: true,       // start immediately
          lastUpdated: Date.now(),
        });
      }
      
    function handleSetPresetPair(workMinutes, breakMinutes) {
        if (!isAdmin) return;
        const presetName =
          workMinutes === 25 && breakMinutes === 5
            ? "pomodoro"
            : workMinutes === 90 && breakMinutes === 30
            ? "deepwork"
            : null;
      
        const newTimerPairs = [
          {
            timer: `${String(workMinutes).padStart(2, "0")}:00`,
            break: `${String(breakMinutes).padStart(2, "0")}:00`,
            timerLabel: "TIMER",
            breakLabel: "BREAK",
          },
        ];
      
        updateRoom({
          mode: "preset",
          selectedPreset: presetName,
          timerPairs: newTimerPairs,
          currentStep: 0,
          currentPhase: "timer",
          baseSeconds: workMinutes * 60,
          timerSeconds: workMinutes * 60,
          running: false,
        });
      }      

    function handleShare() {
        if (roomId) {
            navigate(`/room/${roomId}`, { state: { notification: "New room created and link copied to clipboard!" } });
            navigator.clipboard.writeText(`${window.location.origin}/room/${roomId}`);
        }
    }
    
    

    // Auto-dismiss notification after X seconds
    useEffect(() => {
        if (showNotif) {
            const timer = setTimeout(() => setShowNotif(false), 3000); // 3 seconds
            return () => clearTimeout(timer);
        }
    }, [showNotif]);
    
    

    function handleStart() {
        if (!isAdmin) return;
        const secondsToStart = getLiveSeconds(); // current remainder (or base on new)
        updateRoom({
          running: true,
          timerSeconds: secondsToStart,  //   resume from paused value
          lastUpdated: Date.now(),
        });
      }
      

    function handleStop() {
        if (!isAdmin) return;
        const now = Date.now();
        const elapsed = (now - (sharedState.lastUpdated || now)) / 1000;
        const secondsLeft = Math.max(0, (sharedState?.timerSeconds ?? 0) - elapsed);
        updateRoom({
        running: false,
        timerSeconds: secondsLeft,
        lastUpdated: now,
        });
    }
    function handleReset() {
        if (!isAdmin) return;
        updateRoom({ running: false, baseSeconds, timerSeconds: baseSeconds });
        if (alarmAudioRef.current) {
        alarmAudioRef.current.pause();
        alarmAudioRef.current.currentTime = 0;
        }
    }
    function handleTimeChange(newSeconds) {
        if (!isAdmin) return;
        updateRoom({ baseSeconds: newSeconds, timerSeconds: newSeconds });
    }
    function handleNextTimer() {
        // For future sequence support
    }

    // Live ticking logic
    function getLiveSeconds() {
        if (!sharedState) return 0;
        if (!sharedState.running) {
          // Timer is paused/stopped: always return an integer
          return Math.round(sharedState.timerSeconds ?? 0);
        }
        const now = Date.now();
        const elapsed = (now - (sharedState.lastUpdated || now)) / 1000; // fractional
        return Math.max(0, Math.round((sharedState.timerSeconds ?? 0) - elapsed));
      }

    const resolvedRunning = sharedState?.running ?? running;
    const liveSeconds = getLiveSeconds();
    const timerLabel = getTimerDisplayLabel({
        mode: isAdmin ? mode : sharedState?.mode || mode,
        running: resolvedRunning,
        currentPhase,
        selectedPreset,
        timerPairs,
        currentStep,
    });

    useTimerPictureInPicture({
        running: resolvedRunning,
        timerSeconds: liveSeconds,
        label: timerLabel,
        theme,
    });
           

    if (notFound) {
        return (
        <div className="flex flex-col items-center justify-center min-h-screen text-2xl">
            Room not found!
        </div>
        );
    }

    return (
        <div
        style={{
            background: theme.colors.background,
            color: theme.colors.text,
            minHeight: "100vh",
        }}
        className="flex flex-col"
        >
        <audio id="alarm-audio" src="/alarm.wav" preload="auto" loop ref={alarmAudioRef} />
        <CustomTimerModal
             show={customModalOpen}
             onClose={() => setCustomModalOpen(false)}
             timerPairs={timerPairs}
             setTimerPairs={isAdmin ? setTimerPairs : undefined}
             loopTimers={loopTimers}
             setLoopTimers={isAdmin ? setLoopTimers : undefined}
             onStartSequence={isAdmin ? startCustomSequenceRoom : undefined}
             theme={theme}
        />

        <NotepadModal
            show={notepadModalOpen}
            onClose={() => setNotepadModalOpen(false)}
            theme={theme}
        />

        <Header
            mode={mode}
            setMode={isAdmin ? setMode : undefined}
            setTimerSeconds={isAdmin ? setBaseSeconds : undefined}
            setBaseSeconds={isAdmin ? setBaseSeconds : undefined}
            setRunning={isAdmin ? setRunning : undefined}
            themeKey={themeKey}
            setThemeKey={setThemeKey}
            themes={themes}
            theme={theme}
            displayMode={displayMode}
            openThemeModal={() => setModalOpen(true)}
            setDisplayMode={setDisplayMode}
            onOpenCustomModal={isAdmin ? () => setCustomModalOpen(true) : undefined}
            handleShare={handleShare}
            onSetPresetPair={isAdmin ? handleSetPresetPair : undefined}
            onOpenNotepad={() => setNotepadModalOpen(true)}
        />

        
        <div className="flex flex-1 flex-col items-center justify-center">
            <div className="w-full max-w-2xl px-2 sm:px-6">
            <TimerDisplay
                mode={isAdmin ? mode : sharedState?.mode || mode}
                editing={editing}
                timerSeconds={liveSeconds}
                // Only admin can edit, and only when paused:
                handleTimerClick={
                    isAdmin && !(sharedState?.running ?? running)
                    ? () => setEditing(true)
                    : undefined
                }
                inputValue={inputValue}
                handleInputChange={
                    isAdmin && editing
                    ? (e) => setInputValue(e.target.value)
                    : undefined
                }   
                inputRef={inputRef}
                handleInputBlurOrEnter={
                    isAdmin && editing
                    ? () => {
                        const seconds = parseTime(inputValue);
                        setEditing(false);
                        setInputValue(formatTime(seconds));
                        handleTimeChange(seconds); // update Firestore
                        }
                    : undefined
                }
                handleInputKeyDown={
                    isAdmin && editing
                    ? (e) => {
                        if (e.key === "Enter" || e.key === "Escape") {
                            const seconds = parseTime(inputValue);
                            setEditing(false);
                            setInputValue(formatTime(seconds));
                            if (e.key === "Enter") handleTimeChange(seconds);
                        }
                        }
                    : undefined
                }
                theme={theme}
                displayMode={displayMode}
                currentPhase={currentPhase}
                selectedPreset={selectedPreset}
                running={sharedState?.running ?? running}
                timerPairs={timerPairs}
                currentStep={currentStep}
                onTimeChange={isAdmin ? handleTimeChange : undefined}
                baseSeconds={baseSeconds}
                />

            <TimerControls
                running={resolvedRunning}
                timerSeconds={liveSeconds}
                handleStart={handleStart}
                handleStop={handleStop}
                handleReset={handleReset}
                handleNextTimer={isAdmin ? handleNextTimer : undefined}
                theme={theme}
                mode={mode}
            />
            <ThemeModal
                themes={themes}
                themeKey={themeKey}
                setThemeKey={setThemeKey}
                previewThemeKey={previewThemeKey}
                setPreviewThemeKey={setPreviewThemeKey}
                show={modalOpen}
                onClose={() => setModalOpen(false)}
            />
            </div>
        </div>
        <AnimatePresence>
{showNotif && (
    <motion.div
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -80, opacity: 0 }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
        className="fixed top-0 left-0 w-full z-50 flex justify-center"
    >
        <div
            className="relative mt-16 px-6 py-3 rounded-xl shadow text-base font-semibold flex items-center justify-center"
            style={{
                background: theme.colors.background,
                color: theme.colors.text,
                fontFamily: theme.fontFamily,
                border: `1.5px solid ${theme.colors.text}`,
                minWidth: 320,
            }}
        >
            <div className="w-full text-center">
                Room created and link copied!
            </div>
            <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-lg font-bold hover:opacity-70"
                style={{
                    color: theme.colors.text,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                }}
                onClick={() => setShowNotif(false)}
                aria-label="Close notification"
            >
                ×
            </button>
        </div>
    </motion.div>
)}
</AnimatePresence>
<div className="relative w-full">
  <Footer theme={theme} />
  {roomId && (
    <div
      className="absolute right-3 bottom-2 text-xs text-gray-500"
      style={{ zIndex: 1, pointerEvents: "none" }}
    >
      Room: <code>{roomId}</code> {isAdmin ? "(You are admin)" : "(Viewer)"}
    </div>
  )}
</div>
</div>
    );
    }
